/**
 * <waitlist-form> — the inline "join the waitlist" section.
 *
 * Same signup path as the modals, via SignupClient: Shopify customer form
 * (consent + tags), Klaviyo list, then the waitlist's label appended to the
 * profile's waitlist_products.
 *
 * States: idle → busy → done | error. The page arrives already in the right
 * state: Liquid reads Shopify's `form.posted_successfully?` / `form.errors`
 * after a native submit and renders the done or error markup server-side, so
 * nothing here is needed for that path. This element adds:
 *   - the no-reload submit (fetch), rendering done/error in place
 *   - the "already signed up" wording, which only the URL reveals
 *   - the theme-editor state preview
 * render() just toggles the server-rendered idle/done/error elements; rich
 * text from the editor is shown as-is and nothing is destroyed.
 *
 * Requires signup-client.js (window.SignupClient) to be loaded first.
 */
(() => {
  if (customElements.get('waitlist-form')) return;

  class WaitlistForm extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.form = this.querySelector('form');
      this.body = this.querySelector('[data-state]');
      this.outcome = this.body.dataset.state === 'done' ? 'new' : null; // 'new' | 'existing'

      this.form.addEventListener('submit', this.onSubmit);
      this.boot();
    }

    disconnectedCallback() {
      this.form.removeEventListener('submit', this.onSubmit);
    }

    /* ── lifecycle ──────────────────────────────────────────────────────── */

    boot() {
      // Theme editor: show whichever state the merchant picked, no network.
      const preview = this.dataset.previewState;
      if (preview && preview !== 'idle') {
        this.outcome = preview === 'existing' ? 'existing' : 'new';
        if (preview === 'error') this.render('error', this.config.errorText);
        else this.render('done');
        return;
      }

      // Back from a native submit: Liquid already rendered done/error. The one
      // thing it can't tell is "already signed up", which Shopify signals only
      // in the URL — upgrade to the dedicated wording if there is one.
      if (location.hash !== `#${this.form.id}`) return; // the redirect names the form it came from
      if (SignupClient.outcomeFromUrl(location.href) === 'existing' && this.querySelector('[data-success-existing]')) {
        this.outcome = 'existing';
        this.render('done');
      }
    }

    render(state, error = '') {
      this.body.dataset.state = state;
      const done = state === 'done';
      const busy = state === 'busy';
      const existing = done && this.outcome === 'existing' && this.querySelector('[data-success-existing]');

      for (const el of this.querySelectorAll('[data-idle]')) el.hidden = done;
      for (const el of this.querySelectorAll('[data-done]')) el.hidden = !done;
      if (existing) this.querySelector('[data-success]')?.setAttribute('hidden', '');
      else this.querySelector('[data-success-existing]')?.setAttribute('hidden', '');

      this.querySelector('[data-fields]').hidden = done;
      this.form.querySelector('[type="submit"]').disabled = busy;
      this.form.setAttribute('aria-busy', String(busy));

      const err = this.querySelector('[data-error]');
      err.textContent = error;
      err.hidden = !error;
    }

    /* ── events ─────────────────────────────────────────────────────────── */

    onSubmit = async (event) => {
      event.preventDefault();
      const input = this.form.elements['contact[email]'];
      if (!input.checkValidity()) {
        input.reportValidity();
        return;
      }
      const email = input.value.trim();
      const { companyId, listId } = this.config.klaviyo || {};

      this.render('busy');

      // Klaviyo: best-effort and independent of Shopify's answer, like the
      // modals. List first (consent), then this waitlist onto the profile.
      if (companyId && listId) {
        SignupClient.subscribeToList({ companyId, listId, email, source: this.config.source })
          .then(() => this.config.label && SignupClient.setProfileProperties({
            companyId,
            email,
            append: { waitlist_products: this.config.label },
          }))
          .catch((error) => console.warn('[waitlist-form] klaviyo', error));
      }

      try {
        const outcome = await SignupClient.postCustomerForm(this.form);

        // Bot protection: hand the round-trip to the browser. Shopify brings
        // the page back with the outcome, and Liquid renders it — nothing to
        // remember on this side.
        if (outcome === 'challenge') {
          this.form.submit();
          return;
        }

        this.outcome = outcome;
        this.form.reset();
        this.render('done');
      } catch (error) {
        console.error('[waitlist-form]', error);
        this.render('error', this.config.errorText);
      }
    };
  }

  customElements.define('waitlist-form', WaitlistForm);
})();
